import React, { useRef } from "react";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import { View } from "react-native";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { TouchableWithoutFeedback } from "react-native-web";
import { useTranslation } from "react-i18next";

const ImagePlaceholder = ({
  onFileSelection = () => {},
  imagePath,
  isFileUploadInProgress,
  fileUploadProgress,
  isLargeLogo,
  style,
  wrapperStyleCloseIcon,
  placeholderStyle,
  forcedImgHeight,
  forcedImgWidth,
  forcedImgRadius,
  disabled,
  hintStyle,
  placeholderWrapperIconStyle,
  placeholderIconStyle,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const AppToasty = AppToast.useToast();
  const { currentTheme } = AppTheme.useTheme();
  const [isMouseOver, setIsMouseOver] = React.useState(false);
  const isGraceful = isLargeLogo && !forcedImgWidth && !forcedImgHeight;
  const handlePlaceholderClick = () => {
    fileInputRef.current.click();
  };
  const isValidFileSize = (file) => {
    const maxSizeInBytes = 1 * 1024 * 1024; // 1MB
    return file.size <= maxSizeInBytes;
  };
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!isValidFileSize(file)) {
      return AppToasty.show(
        "File size exceeds 1MB. Please upload a smaller image.",
        {
          type: "danger",
        }
      );
    }
    // Handle the uploaded file here
    // For now, let's just log it to the console
    onFileSelection(file);

    // Implement your image validation logic here
  };

  return (
    <TouchableWithoutFeedback
      disabled={disabled}
      onPress={() => fileInputRef.current.click()}
    >
      <View
        style={[
          {
            cursor: "pointer",
            border:
              !imagePath &&
              `1px solid ${
                currentTheme?.theme[
                  isFileUploadInProgress ? "orange" : "primary"
                ]
              }`,
            padding: !imagePath && 5,
            textAlign: "center",
            width: isLargeLogo ? (isGraceful ? "fit-content" : 128) : 36,
            height: isLargeLogo ? "auto" : 36,
            alignSelf: "center",
            borderRadius: 20,
            alignItems: "center",
          },
          style,
        ]}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleImageUpload}
          // limit the input to only accept images
          accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/bmp"
        />
        {imagePath && !isFileUploadInProgress ? (
          <div
            style={{ position: "relative", width: "fit-content" }}
            onMouseEnter={() => {
              setIsMouseOver(true);
            }}
            onMouseLeave={() => {
              setIsMouseOver(false);
            }}
          >
            {isMouseOver && !disabled && (
              <AppIcon
                icon={AppAssets.icons.close}
                size={12}
                color={currentTheme?.theme?.red}
                wrapperStyle={[
                  {
                    position: "absolute",
                    top: -7,
                    right: -7,
                    zIndex: 1,
                  },
                  wrapperStyleCloseIcon,
                ]}
                isClickable
                onPress={() => {
                  onFileSelection(null);
                }}
              />
            )}
            <img
              src={imagePath}
              style={{
                borderRadius: forcedImgRadius || 20,
                border: `1px solid ${currentTheme?.theme?.primary}`,
                // Graceful mode for large logos without forced dims: keep the
                // image's natural aspect ratio inside a capped box instead of
                // squishing it into a fixed square.
                ...(isLargeLogo && !forcedImgWidth && !forcedImgHeight
                  ? { maxWidth: 240, maxHeight: 120, objectFit: "contain" }
                  : {}),
              }}
              onClick={(e) => {
                e.stopPropagation();
                !disabled && fileInputRef.current.click();
              }}
              alt="Uploaded icon"
              {...(isLargeLogo && !forcedImgWidth && !forcedImgHeight
                ? {}
                : {
                    width: forcedImgWidth || 36 * (isLargeLogo ? 3 : 1),
                    height: forcedImgHeight || (isLargeLogo ? "auto" : 36),
                  })}
            />
          </div>
        ) : isFileUploadInProgress && fileUploadProgress !== 100 ? (
          <AppText
            style={{
              fontSize: 14,
              fontWeight: "bold",
            }}
            color={currentTheme?.theme?.orange}
          >{`${fileUploadProgress}%`}</AppText>
        ) : (
          <View style={placeholderStyle}>
            <AppIcon
              wrapperStyle={placeholderWrapperIconStyle}
              style={[
                {
                  alignSelf: "center",
                },
                placeholderIconStyle,
              ]}
              isClickable
              onPress={() => {
                !disabled && fileInputRef.current.click();
              }}
              icon={AppAssets.icons.image}
              size={24}
            />
            <AppText
              style={[
                {
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "max-content",
                  marginTop: 10,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                },
                hintStyle,
              ]}
              color={currentTheme?.theme?.darkGrey}
            >
              {t("ui.uploadImage")}
            </AppText>
          </View>
        )}
      </View>
    </TouchableWithoutFeedback>
  );
};

export default ImagePlaceholder;
